import { css, type Theme } from '@emotion/react';
import { transparentize } from 'polished';
import { WarningCircle } from '@phosphor-icons/react';
import type { StackConfigErrorCode } from 'common/stack/StackConfigDocuments';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { Stack } from 'client/components/Stack';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { PendingChangesComponents } from './PendingChangesComponents';

const errorCodeToTitle: Record<StackConfigErrorCode, string> = {
  'component error': 'Component Error',
  'validation error': 'Validation Error',
  'yaml error': 'Parse Error',
  'package url error': 'Component Error',
  'config load failed': 'Component Error',
  'unexpected error': 'Unexpected Error',
};

const errorCodeToDescription: Record<StackConfigErrorCode, string | JSX.Element> = {
  'component error': (
    <>
      A component in the local <Tag type="grey">diploi.yaml</Tag> file has an error.
    </>
  ),
  'validation error': (
    <>
      The local <Tag type="grey">diploi.yaml</Tag> file contains errors and cannot be parsed.
    </>
  ),
  'yaml error': (
    <>
      The local <Tag type="grey">diploi.yaml</Tag> file contains errors and cannot be parsed.
    </>
  ),
  'package url error': (
    <>
      A component in the local <Tag type="grey">diploi.yaml</Tag> file has an invalid URL.
    </>
  ),
  'config load failed': (
    <>
      A component in the local <Tag type="grey">diploi.yaml</Tag> file could not be loaded.
    </>
  ),
  'unexpected error': (
    <>
      An error occurred while processing the local <Tag type="grey">diploi.yaml</Tag> file.
    </>
  ),
};

export const PendingChangesErrorCard = () => {
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const statusTree = useDeploymentStatusTree(deploymentID);

  if (!statusTree || !statusTree.pendingChanges) return null;

  const { state, errorCode = 'unexpected error', errorDetails, components, timeUpdated } = statusTree.pendingChanges;

  if (state !== 'error') return null;

  return (
    <Stack css={cardStyle}>
      <div css={headerStyle}>
        <Typography variant="h6">
          <WarningCircle weight="bold" /> {errorCodeToTitle[errorCode as StackConfigErrorCode]}
        </Typography>
        <Typography variant="body2">{errorCodeToDescription[errorCode as StackConfigErrorCode]}</Typography>
      </div>

      {/* General error */}
      {errorDetails && (
        <Typography variant="body2" css={errorStyle}>
          {errorDetails}
        </Typography>
      )}

      {/* Individual component errors */}
      {components
        .filter((component) => component.errorDetails)
        .map((component) => (
          <Typography key={component.stackComponentID || component.identifier} variant="body2" css={errorStyle}>
            <label>
              <WarningCircle weight="fill" /> {component.identifier}
            </label>
            {component.errorDetails}
          </Typography>
        ))}

      <PendingChangesComponents components={components} />
    </Stack>
  );
};

const cardStyle = (theme: Theme) => css`
  padding: 26px;
  border-radius: ${theme.borderRadius}px;
  border: 2px solid ${theme.color.red[100]};
  background: linear-gradient(
    -5deg,
    ${theme.color.background},
    ${transparentize(theme.type === 'light' ? 0.35 : 0, theme.color.red[60])} 90%
  );
  box-sizing: border-box;

  // FIXME: This should be outside of this component
  margin-bottom: 32px;
`;

const headerStyle = (theme: Theme) => css`
  > h6 {
    display: flex;
    align-items: center;
    gap: 8px;

    > svg {
      color: ${theme.color.red[100]};
      translate: 0 -2px;
    }
  }
`;

const errorStyle = (theme: Theme) => css`
  padding: 12px 16px;
  font-family: ${theme.font.code};
  font-size: 12px !important;
  background-color: ${theme.color.red[60]};
  border-radius: ${theme.borderRadius}px;
  color: ${theme.color.red.text} !important;

  > label {
    display: flex;
    align-items: center;
    font-weight: bold;
    gap: 4px;
    margin-bottom: 4px;

    > svg {
      color: ${theme.color.red[100]};
    }
  }
`;
